import React, { useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { DEV_ENV_FLAG, QR_TYPE } from '../../../utils/Constants';

import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import SmallLoading from '../../../components/SmallLoading/SmallLoading';
import SampleMark from '../../../components/SampleMark/SampleMark';
import ClockDigital from '../../../components/clock-digital/ClockDigital';
import TicketDisplay from '../../../components/TicketDisplay/TicketDisplay';

import styles from './styles.module.css';

import icBack from '../../../assets/imgs/ic_btn_back.svg?react';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import useDisplayTicket from '../../../hooks/useDisplayTicket';
import QrCodeDisplay from '../../../components/QrCodeDisplay/QrCodeDisplay';
import { OrderTicket } from 'types';

import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * 企画券の券面表示
 */
const TicketDisplayScreen = memo(() => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useFujiAuth();
    const location = useLocation();
    const { orderId, orderTicketId, distributionQrCode } = location?.state ?? {};
    const { distributionCode, redirectUrl } = distributionQrCode ?? {};
    const {
        orderTicket,
        fetchOrderTicket,
        isLoading,
        smallLoading,
        TimeOverModal,
        DisplayEndModal,
        fetchDistributionQrCode,
    } = useDisplayTicket();

    /**
     * チケット表示画面を表示した時
     */
    useEffect(() => {
        //QRコードWeb表示中継ページから遷移する場合は
        if (distributionCode) {
            void fetchDistributionQrCode({ distributionCode });
        } else if (isAuthenticated && orderId && orderTicketId) {
            //有効化された券の表示
            void fetchOrderTicket(orderId, orderTicketId);
        } else {
            // 購入ページに遷移
            navigate('/buy');
        }
    }, [isAuthenticated, orderId, orderTicketId, navigate, fetchOrderTicket, distributionCode]);

    /**
     * 券面をタップした時の挙動
     */
    const handlePressTicketActiveContainer = () => {
        dispatch({ type: 'CHANGE_ACTIVE_THEME' });
    };

    /**
     * 戻るボタンの処理
     * @returns {void}
     */

    const handleBackButton = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
        event.stopPropagation();
        navigate(-1);
    };

    return (
        <>
            <SmallLoading show={smallLoading} content={t('loading.small-bought-ticket') ?? ''} />
            <div className={styles.displayTicketWrapper} onClick={handlePressTicketActiveContainer}>
                {isLoading && <CenterLoading overFooter />}
                <div className={styles.invisibleHeader}>
                    <div className={styles.backIconView} onClick={handleBackButton}>
                        {React.createElement(icBack, { className: styles.backIcon }) ?? 'Back'}
                    </div>
                </div>
                {orderTicket ? (
                    <>
                        {DEV_ENV_FLAG && (
                            <div
                                className={styles.sampleMark}
                                style={
                                    orderTicket?.qrType === QR_TYPE.SINGLE_SCAN || distributionQrCode
                                        ? { bottom: '30%' }
                                        : {}
                                }
                            >
                                <SampleMark />
                            </div>
                        )}
                        <div className={styles.ticketContainer}>
                            {orderTicket?.qrType === QR_TYPE.SINGLE_SCAN || distributionQrCode ? (
                                <>
                                    <QrCodeDisplay orderTicket={orderTicket as OrderTicket} redirectUrl={redirectUrl} />
                                    {/**
                                     * DisplayEndModal -> QrCodeDisplay内で別途定義済み
                                     * TimeOverModal -> 有効期限切れは、web-qr-displayから遷移した場合に必要な情報がないため、QRの場合は表示しない
                                     */}
                                </>
                            ) : (
                                <>
                                    <TicketDisplay orderTicket={orderTicket as OrderTicket} />
                                    <DisplayEndModal />
                                    <TimeOverModal />
                                    {/**
                                     * QRコードが認証されるとorderUsageTicket.expiredAtが更新されるため、QRチケットではない場合のみTimeOverModalを表示すようにここに配置
                                     */}
                                </>
                            )}
                        </div>
                        <div className={styles.clockDigitalContainer}>
                            <ClockDigital orderTicket={orderTicket as OrderTicket} />
                        </div>
                    </>
                ) : (
                    <NoTicketView loading={smallLoading} />
                )}
            </div>
        </>
    );
});

/**
 * 表示するチケットがない
 */
const NoTicketView = ({ loading }: { loading: boolean }) => {
    const { t } = useTranslation();
    return (
        <div className={styles.noTicketView}>
            <p> {loading ? t('loading.small-bought-ticket') : t('ticket-display.no-ticket')}</p>
        </div>
    );
};

export default TicketDisplayScreen;
